import { useEffect, useState } from 'react';

// components
import Image from 'next/image';
import RichText from '@/components/utilities/RichText';
import { Button } from '@/components/ui/button';
import Link from 'next/link';
import { Icon } from '@/components/ui/icon';
import VisibleLoading from '@/components/utilities/VisibleLoading';

// utilities
import { getImagePath } from '@/utils/images';

const HeroBlock = ({
  background,
  title,
  subtitle,
  titleButtons,
  claimTitle,
  claimSubtitle,
  claimNote,
  references,
  testimonials
}) => {
  // Client-side only random selection to avoid hydration mismatch
  const [testimonial, setTestimonial] = useState(null);

  useEffect(() => {
    if (testimonials?.length) {
      setTimeout(() => {
        const randomIndex = Math.floor(Math.random() * testimonials.length);

        setTestimonial(testimonials[randomIndex]);
      }, 0);
    }
  }, [testimonials]);

  return (
    <div data-variant="dark" className="relative w-full">
      {background && (
        <Image
          src={getImagePath(background)}
          alt={background.alternativeText}
          className="-z-10 hidden object-cover xl:block"
          fill={true}
        />
      )}

      <div
        className="flex w-full flex-col items-center"
        style={{
          background: `
            radial-gradient(47.49% 15.03% at 29.4% 50%, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.00) 100%),
            linear-gradient(180deg, rgba(0, 0, 0, 0.40) 0%, rgba(0, 0, 0, 0.28) 6.46%, rgba(0, 0, 0, 0.00) 11.99%),
            linear-gradient(0deg, rgba(0, 0, 0, 0.30) 0%, rgba(0, 0, 0, 0.30) 100%)
          `
        }}
      >
        <div className="relative w-full">
          {background && (
            <Image
              src={getImagePath(background)}
              alt={background.alternativeText}
              className="-z-10 object-cover xl:hidden"
              fill={true}
            />
          )}

          <div className="mx-auto flex min-h-128 w-[90vw] grow flex-col justify-center text-white xl:h-screen xl:w-(--container-width)">
            <VisibleLoading delay={75}>
              {({ ref, className }) => (
                <h1
                  ref={ref}
                  className={`mb-4 max-w-[812px] text-center font-bold font-light xl:text-left xl:text-[100px] xl:leading-[120%] ${className || ''}`.trim()}
                >
                  {title}
                </h1>
              )}
            </VisibleLoading>

            <VisibleLoading delay={150}>
              {({ ref, className }) => (
                <p
                  ref={ref}
                  className={`txt-xl mt-6 max-w-[485px] text-center font-medium xl:text-left ${className || ''}`.trim()}
                >
                  {subtitle}
                </p>
              )}
            </VisibleLoading>
            {(titleButtons || []).length > 0 && (
              <div className="mt-12 flex flex-wrap gap-2">
                {titleButtons.map((button, index) => (
                  <VisibleLoading key={button.id} delay={(index + 1) * 200}>
                    {({ ref, className }) => (
                      <Button
                        ref={ref}
                        variant={button.variant}
                        size={button.size}
                        asChild={true}
                        className={`basis-full xl:basis-auto ${className || ''}`.trim()}
                      >
                        <Link href={button.url || ''}>
                          <Icon type={button.icon} /> {button.label}
                        </Link>
                      </Button>
                    )}
                  </VisibleLoading>
                ))}
              </div>
            )}
          </div>

          {testimonial?.testimonial && (
            <VisibleLoading delay={300}>
              {({ ref, className }) => (
                <div ref={ref} className={`mx-auto mt-0 mb-6 w-[90vw] xl:hidden ${className || ''}`}>
                  <Testimonial testimonial={testimonial} className="flex" />
                </div>
              )}
            </VisibleLoading>
          )}
        </div>

        <div
          data-variant="light"
          data-variant-max-width="1280"
          className="relative w-full bg-white pt-20 pb-20 text-black xl:bg-transparent xl:bg-[linear-gradient(180deg,_rgba(17,7,28,0.00)_0%,_rgba(29,2,56,0.54)_40.87%,_#512182_100%)] xl:pb-32 xl:text-white"
        >
          {testimonial?.testimonial && (
            <VisibleLoading delay={300}>
              {({ ref, isVisible }) => (
                <Testimonial
                  innerRef={ref}
                  testimonial={testimonial}
                  className={
                    isVisible
                      ? `ease absolute top-0 right-12 hidden translate-x-0 -translate-y-[calc(100%+40px)] opacity-100 transition delay-1000 duration-700 will-change-[opacity,transform] xl:flex`
                      : `ease absolute top-0 right-12 hidden translate-x-12 -translate-y-[calc(100%+40px)] opacity-0 transition delay-1000 duration-700 will-change-auto xl:flex`
                  }
                />
              )}
            </VisibleLoading>
          )}

          <div className="mx-auto w-[90vw] text-center xl:w-(--container-width)">
            <VisibleLoading delay={75}>
              {({ ref, className }) => (
                <h2 ref={ref} className={className}>
                  <RichText>{claimTitle}</RichText>
                </h2>
              )}
            </VisibleLoading>

            <VisibleLoading delay={150}>
              {({ ref, className }) => (
                <div ref={ref} className={`txt-xl mt-4 ${className || ''}`.trim()}>
                  <RichText>{claimSubtitle}</RichText>
                </div>
              )}
            </VisibleLoading>

            {claimNote && (
              <VisibleLoading delay={250}>
                {({ ref, className }) => (
                  <div ref={ref} className={`txt-md mt-4 ${className || ''}`.trim()}>
                    <RichText>{claimNote}</RichText>
                  </div>
                )}
              </VisibleLoading>
            )}
          </div>

          {(references || []).length > 0 && (
            <div className="no-scrollbar mx-auto mt-16 flex w-full items-center gap-12 overflow-x-auto px-4 py-4 opacity-50 mix-blend-difference xl:mt-20 xl:w-(--container-width) xl:flex-wrap xl:justify-center xl:overflow-x-visible xl:px-0 xl:py-0 xl:opacity-100 xl:mix-blend-normal">
              {references.map((reference, index) => (
                <VisibleLoading key={reference.id} delay={index * 100} opacity={50}>
                  {({ ref, className }) => (
                    <Image
                      ref={ref}
                      src={getImagePath(reference.image)}
                      alt={reference.name}
                      width={reference.image.width}
                      height={reference.image.height}
                      className={`max-h-8 w-auto object-contain xl:h-full xl:max-h-6 ${className || ''}`.trim()}
                    />
                  )}
                </VisibleLoading>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default HeroBlock;

const Testimonial = ({ innerRef, testimonial, className }) => {
  return (
    <div
      ref={innerRef}
      className={`min-h-[280px] max-w-[451px] flex-col justify-between rounded-3xl border border-white/[20%] bg-black/[15%] p-6 backdrop-blur-[10px] xl:min-h-[310px] xl:w-[451px] xl:p-10 ${className}`}
    >
      <div>
        <div className="text-brand-400 mb-6 h-[28px] text-[135px] xl:mb-8">
          <svg width="39" height="28" viewBox="0 0 39 28" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M16.84 20.025C16.84 24.48 13.735 27.855 9.01 27.855C4.285 27.855 0.235001 24.075 0.235001 17.325C0.235001 8.95499 6.58 1.8 14.815 0.449997V5.445C10.09 6.38999 6.58 9.765 6.58 13.68C7.39 13.14 8.47 12.735 10.225 12.735C13.735 12.735 16.84 15.3 16.84 20.025ZM38.71 20.025C38.71 24.48 35.47 27.855 30.745 27.855C26.155 27.855 21.97 24.075 21.97 17.325C21.97 8.95499 28.315 1.8 36.685 0.449997V5.445C31.96 6.38999 28.45 9.765 28.45 13.545C29.26 13.005 30.475 12.735 32.095 12.735C35.605 12.735 38.71 15.3 38.71 20.025Z"
              className="fill-brand-400"
            />
          </svg>
        </div>
        <div className="txt-lg text-white">{testimonial.testimonial.message}</div>
      </div>
      <div className="mt-8 flex items-center gap-3">
        <div className="h-12 w-12 overflow-hidden rounded-full bg-white/[20%]">
          {testimonial.testimonial.image && (
            <Image
              src={getImagePath(testimonial.testimonial.image)}
              alt={testimonial.testimonial.name}
              width={testimonial.testimonial.image.width}
              height={testimonial.testimonial.image.height}
              className="h-full w-full object-cover"
            />
          )}
        </div>
        <div>
          <div className="txt-xl font-medium text-white">{testimonial.testimonial.name}</div>
          <div className="txt-lg text-white/[75%]">{testimonial.testimonial.company}</div>
        </div>
      </div>
    </div>
  );
};
